import {
  Divider,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup
} from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { useState } from 'react'
import { FormattedMessage } from 'react-intl'
import {
  AddressLocationType,
  AlertComponent,
  CommonFieldProps,
  ContentWrapper,
  FormikSelect,
  FormikTextField,
  LabelAndDateRange,
  LocalizedCombobox,
  SelectOption
} from '../../../../../components'
import { isArrayWithLength } from '../../../../../utils'
import { AddressType } from '../../../../../utils/classes/graphql'
import {
  OwnInfoDeleteButtonWrapper,
  OwnInfoDeleteButtonWrapperProps
} from './own-info-delete-button-wrapper'
import {
  getContactOptions,
  isAddressLocationDomestic,
  isAddressTemporaryType
} from '../utils'
import { PostalInfo } from './postal-info'
import { useFieldProps } from '../../../../../hooks'
import {
  AddressUpdateEventType,
  AddressUpdateFieldType,
  useAddressUpdateFn
} from '../hooks/useAddressUpdateFn'
import { BaseDetailsAddress } from '../types'

const useStyles = makeStyles(() => ({
  spaceUpTop: {
    marginTop: '0.5rem'
  },
  dividerMargin: {
    marginBottom: '1rem',
    marginTop: '1rem'
  }
}))

interface AddressInfoSelectProps extends CommonFieldProps {
  addressTypeValues: AddressType[]
  isDisabled: boolean
}

const AddressInfoSelect = ({
  addressTypeValues,
  name,
  isDisabled,
  executeOnChangeFn
}: AddressInfoSelectProps) => {
  const addressOptions: SelectOption[] = getContactOptions(
    addressTypeValues,
    'addresses'
  )

  return (
    <ContentWrapper showContent={isArrayWithLength(addressOptions)}>
      <FormikSelect
        name={`${name}.tyyppi`}
        disabled={isDisabled}
        options={addressOptions}
        labelTranslationId="pages.ownInfoPage.baseDetails.addresses.type"
        executeOnChangeFn={executeOnChangeFn}
      />
    </ContentWrapper>
  )
}

interface AddressInfoProps
  extends CommonFieldProps,
    Pick<OwnInfoDeleteButtonWrapperProps, 'arrayHelpers'> {
  address: BaseDetailsAddress
}

export const AddressInfo = ({
  address,
  arrayHelpers,
  name,
  executeOnChangeFn
}: AddressInfoProps) => {
  const { value: addressValue } = useFieldProps<BaseDetailsAddress>(name, false)

  const classes = useStyles()

  const addressTypeValues = Object.values(AddressType)

  const [isDisabled, setDisabled] = useState(false)

  const addressLocationTypes = [
    {
      translationId: 'pages.ownInfoPage.baseDetails.addresses.types.domestic',
      value: AddressLocationType.DOMESTIC
    },
    {
      translationId: 'pages.ownInfoPage.baseDetails.addresses.types.foreign',
      value: AddressLocationType.FOREIGN
    }
  ]

  const isValiaikosLt = isAddressTemporaryType(address?.tyyppi)

  const isDomesticAddress = isAddressLocationDomestic(
    addressValue.addressLocation
  )

  const isCountryCodeDomestic = isAddressLocationDomestic(addressValue.maaKoodi)

  const updateAddress = useAddressUpdateFn(name)

  const onAddressLocationChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateAddress({
      event,
      eventType: AddressUpdateEventType.CHANGE,
      fieldType: AddressUpdateFieldType.ADDRESSLOCATION
    })
  }

  const onCountryCodeChange = (selectedCountryCode: string) => {
    updateAddress({
      fieldType: AddressUpdateFieldType.COUNTRYCODE,
      selectedValue: selectedCountryCode
    })
  }

  return (
    <>
      <Grid container spacing={4}>
        <AddressInfoSelect
          name={name}
          isDisabled={isDisabled}
          addressTypeValues={addressTypeValues}
          executeOnChangeFn={executeOnChangeFn}
        />
        <Grid item md={4} xs={12}>
          <RadioGroup
            aria-label="addressLocation"
            name={`${name}.addressLocation`}
            onChange={(e) => onAddressLocationChange(e)}
            row
            value={addressValue.addressLocation}
          >
            {addressLocationTypes.map((type, index) => {
              return (
                <FormControlLabel
                  control={<Radio color="secondary" />}
                  key={`address-location-${index}-${type.value}`}
                  label={<FormattedMessage id={type.translationId} />}
                  disabled={isDisabled}
                  value={type.value}
                />
              )
            })}
          </RadioGroup>
        </Grid>
        <LocalizedCombobox
          executeOnChangeFn={onCountryCodeChange}
          hidefield={isDomesticAddress}
          name={`${name}.maaKoodi`}
          disabled={isDisabled}
          localizedOptionsKey="countryOptions"
          labelTranslationId="pages.ownInfoPage.baseDetails.addresses.country"
          required
        />
      </Grid>
      <Grid container spacing={4} className={classes.spaceUpTop}>
        <OwnInfoDeleteButtonWrapper
          arrayHelpers={arrayHelpers}
          isDisabled={isDisabled}
          name={name}
          setDisabled={setDisabled}
        >
          <FormikTextField
            disabled={isDisabled}
            executeOnChangeFn={executeOnChangeFn}
            labelTranslationId="pages.ownInfoPage.baseDetails.addresses.street"
            name={`${name}.katuosoite`}
            required
          />
          <PostalInfo
            executeOnChangeFn={executeOnChangeFn}
            name={name}
            postalAreaDisabled={
              isDomesticAddress || isCountryCodeDomestic || isDisabled
            }
            postalNumberDisabled={isDisabled}
          />
          <Grid item md={6}>
            <LabelAndDateRange
              endDateProps={{
                disabled: isDisabled,
                labelTranslationId:
                  'pages.ownInfoPage.baseDetails.addresses.temporaryAddress.endDate',
                required: isValiaikosLt,
                name: `${name}.paattymispvm`,
                executeOnChangeFn
              }}
              hideRange={!isValiaikosLt}
              labelProps={{
                translationId:
                  'pages.ownInfoPage.baseDetails.addresses.temporaryAddress.valid'
              }}
              startDateProps={{
                disabled: isDisabled,
                labelTranslationId:
                  'pages.ownInfoPage.baseDetails.addresses.temporaryAddress.startDate',
                required: isValiaikosLt,
                name: `${name}.voimaantulopvm`,
                executeOnChangeFn
              }}
              spacing={4}
            />
          </Grid>
          <AlertComponent
            showAlert={address.virheellinen === true}
            labelTranslationId="pages.ownInfoPage.baseDetails.addresses.faulty"
          />
        </OwnInfoDeleteButtonWrapper>
      </Grid>
      <Divider className={classes.dividerMargin} />
    </>
  )
}
